import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium",
    "transition-[background,border,color] duration-150",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-600",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          // Primary orange CTA — flat, no gradient, inner highlight
          "bg-[#ff5c35] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] hover:bg-[#ff7654]",
        destructive:
          "bg-rose-600 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-rose-500",
        outline:
          "border border-zinc-800 bg-zinc-950/40 text-zinc-200 hover:border-zinc-700 hover:bg-zinc-900",
        secondary:
          "bg-zinc-900 text-zinc-200 border border-zinc-800 hover:bg-zinc-800",
        ghost:
          "text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100",
        link:
          "text-[#ff5c35] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-6",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
